import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Close, Medication, LocalPharmacy } from "@mui/icons-material";
import colors from "../../../config/colors";

const DetalleAutorizacionModal = ({ 
  open, 
  onClose, 
  detalle, 
  loading = false,
  authNumber 
}) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
    }).format(amount);
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Medication color="primary" />
            <Typography variant="h6">Cargando detalle...</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography>Obteniendo información del detalle...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!detalle || detalle.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <Medication color="primary" />
              <Typography variant="h6">Detalle de Autorización</Typography>
            </Box>
            <Button onClick={onClose} color="primary">
              <Close />
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography color="textSecondary">
              No se encontraron detalles para esta autorización
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <LocalPharmacy sx={{ color: colors.primary }} />
            <Typography variant="h6" sx={{ color: colors.primary, fontWeight: 600 }}>
              Detalle de Autorización #{authNumber}
            </Typography>
          </Box>
          <Button onClick={onClose} sx={{ color: colors.gray, minWidth: "auto", padding: 1 }}>
            <Close />
          </Button>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ padding: "16px 24px" }}>
        {/* Header con resumen */}
        <Box 
          sx={{ 
            backgroundColor: colors.grayFondo,
            padding: 2,
            borderRadius: 2,
            marginBottom: 3,
            border: `1px solid ${colors.grayLight}`
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <Typography variant="body2" sx={{ color: colors.gray, fontSize: "0.9rem" }}>
                Total de medicamentos
              </Typography>
              <Typography variant="h6" sx={{ color: colors.primary, fontWeight: 700 }}>
                {detalle.length}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" sx={{ color: colors.gray, fontSize: "0.9rem" }}>
                Monto total autorizado
              </Typography>
              <Typography variant="h6" sx={{ color: colors.primary, fontWeight: 700 }}>
                {formatCurrency(
                  detalle.reduce((total, med) => total + med.montoAutorizado, 0)
                )}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" sx={{ color: colors.gray, fontSize: "0.9rem" }}>
                Total copago del paciente
              </Typography>
              <Typography variant="h6" sx={{ color: colors.gray, fontWeight: 700 }}>
                {formatCurrency(
                  detalle.reduce((total, med) => total + med.montoCopago, 0)
                )}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Lista de medicamentos como tabla */}
        <Typography variant="h6" sx={{ color: colors.gray, marginBottom: 2, fontWeight: 600 }}>
          Medicamentos Autorizados
        </Typography>
        
        <TableContainer component={Paper} sx={{ boxShadow: "none", border: `1px solid ${colors.grayLight}` }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.primary }}>
                <TableCell sx={{ color: "white", fontWeight: 600, fontSize: "0.9rem" }}>
                  Medicamento
                </TableCell>
                <TableCell align="center" sx={{ color: "white", fontWeight: 600, fontSize: "0.9rem" }}>
                  Código
                </TableCell>
                <TableCell align="center" sx={{ color: "white", fontWeight: 600, fontSize: "0.9rem" }}>
                  Plan
                </TableCell>
                <TableCell align="center" sx={{ color: "white", fontWeight: 600, fontSize: "0.9rem" }}>
                  Cant.
                </TableCell>
                <TableCell align="right" sx={{ color: "white", fontWeight: 600, fontSize: "0.9rem" }}>
                  Reclamado
                </TableCell>
                <TableCell align="right" sx={{ color: "white", fontWeight: 600, fontSize: "0.9rem" }}>
                  Autorizado
                </TableCell>
                <TableCell align="right" sx={{ color: "white", fontWeight: 600, fontSize: "0.9rem" }}>
                  Copago
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detalle.map((medicamento, index) => (
                <TableRow 
                  key={index}
                  sx={{ 
                    '&:nth-of-type(odd)': { 
                      backgroundColor: colors.grayFondo 
                    },
                    '&:hover': {
                      backgroundColor: `${colors.primary}10`
                    }
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: colors.gray }}>
                        {medicamento.descripcion}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ color: colors.gray, fontFamily: "monospace" }}>
                      {medicamento.codSimon}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ color: colors.primary, fontWeight: 600, fontSize: "0.8rem" }}>
                      {medicamento.descripcionPlan || medicamento.codPlan}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ color: colors.gray, fontWeight: 600 }}>
                      {medicamento.cantidad}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ color: colors.gray }}>
                      {formatCurrency(medicamento.montoReclamado)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ color: colors.primary, fontWeight: 700 }}>
                      {formatCurrency(medicamento.montoAutorizado)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ color: colors.gray, fontWeight: 600 }}>
                      {formatCurrency(medicamento.montoCopago)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px" }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          sx={{ 
            backgroundColor: colors.primary,
            color: "white",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: colors.primaryDark
            }
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetalleAutorizacionModal;